import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useAuth } from '@/context/AuthContext'

export type CrmRole = 'admin' | 'employee' | 'client'

interface CrmRoleValue {
  role: CrmRole
  isAdmin: boolean
  user: { name: string; email: string; initials: string }
}

const CrmRoleContext = createContext<CrmRoleValue | null>(null)

/** Prefers the split name columns; falls back to splitting a display string. */
function initials(first: string | null, last: string | null, fallback: string) {
  if (first || last) return (first?.[0] ?? '') + (last?.[0] ?? '')
  const parts = fallback.trim().split(/\s+/)
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')
}

export function CrmRoleProvider({ children }: { children: ReactNode }) {
  const { user, profile, isAdmin, isStaff } = useAuth()

  const value = useMemo<CrmRoleValue>(() => {
    const role: CrmRole = isAdmin ? 'admin' : isStaff ? 'employee' : 'client'
    const name = profile?.full_name || user?.email || ''
    return {
      role,
      isAdmin,
      user: {
        name,
        email: profile?.email ?? user?.email ?? '',
        initials:
          initials(profile?.first_name ?? null, profile?.last_name ?? null, name).toUpperCase() ||
          '?',
      },
    }
  }, [isAdmin, isStaff, profile, user])

  return <CrmRoleContext.Provider value={value}>{children}</CrmRoleContext.Provider>
}

export function useCrmRole() {
  const ctx = useContext(CrmRoleContext)
  if (!ctx) throw new Error('useCrmRole must be used within CrmRoleProvider')
  return ctx
}
